import {Inject, Injectable} from '@angular/core';
import {FirebaseAuthentication} from '@capacitor-firebase/authentication';
import {NGXLogger} from 'ngx-logger';
import {Store} from '@ngrx/store';
import {userLoad} from 'gamesis-state';
import {initializeApp} from 'firebase/app';
import {Capacitor} from '@capacitor/core';
import {AUTH_CONFIG} from './auth.config';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    AUTH_NAME = 'authorizations';

    constructor(private logger: NGXLogger,
                private store: Store,
                @Inject(AUTH_CONFIG) private options: any) {
        if (!Capacitor.isNativePlatform()) {
            initializeApp(options);
        }
    }

    public async initialize(): Promise<void> {
        await FirebaseAuthentication.removeAllListeners();
        await FirebaseAuthentication.addListener('authStateChange', (change) => {
            this.store.dispatch(userLoad({user: change.user}));
        });
        try {
            await FirebaseAuthentication.reload();
            const user = await FirebaseAuthentication.getCurrentUser();
            this.store.dispatch(userLoad({user: user.user}));
        } catch (error) {
        }

        this.logger.debug('initialized auth service');

        return Promise.resolve();
    }

    openAppleSignIn(): void {
        FirebaseAuthentication.signInWithApple().then(result => {
            this.logger.debug(`logged in with apple`);
        });
    }

    openGoogleSignIn(): void {
        FirebaseAuthentication.signInWithGoogle().then(result => {
            this.logger.debug(`logged in with google`);
        });
    }

    firebaseSignin(credentials: any): Promise<void> {
        return new Promise((resolve, reject) => {
            FirebaseAuthentication.signInWithEmailAndPassword(credentials).then(result => {
                if (result.user?.emailVerified) {
                    // this.notifications.success('Logged in successfully');
                    resolve();
                } else {
                    FirebaseAuthentication.sendEmailVerification().then(() => {
                        // this.notifications.warning('Email has not been verified.');
                        resolve();
                    }).catch(() => {
                        reject('Unable to send verification');
                    });
                }
            }).catch(() => {
                reject();
            });
        });
    }

    register(credentials: any): Promise<void> {
        return new Promise((resolve, reject) => {
            FirebaseAuthentication.createUserWithEmailAndPassword(credentials).then(result => {
                FirebaseAuthentication.sendEmailVerification().then(() => {
                    resolve();
                }).catch(() => {
                    reject('Unable to send verification');
                });
            }).catch((error) => {
                switch (error.code) {
                    case 'auth/email-already-in-use':
                        reject('Email address is already in use.');
                        break;
                    case 'auth/invalid-email':
                        reject('Invalid email address.');
                        break;
                    case 'auth/password-does-not-meet-requirements':
                        reject('The password does not meet the requirements.');
                        break;
                    default:
                        reject(`Unexpected error: ${error}`);
                }
            });
        });
    }

    recover(email: string): Promise<void> {
        return new Promise((resolve, reject) => {
            FirebaseAuthentication.sendPasswordResetEmail({
                email: email
            }).then(result => {
                resolve();
            }).catch((error) => {
                reject(`Unexpected error: ${error}`);
            });
        });
    }

    logout(): Promise<void> {
        return FirebaseAuthentication.signOut();
    }

    deleteUser(): void {
        FirebaseAuthentication.deleteUser().then(() => {
            this.logger.debug(`user deleted`);
            this.logout();
        });
    }

    tokenGetter(): Promise<string | null> {
        return new Promise((resolve, reject) => {
            FirebaseAuthentication.getIdToken().then(result => {
                this.logger.debug(`retrieved id token`);
                resolve(result.token);
            }, reason => {
                this.logger.warn(`unable to get token: ${reason}`);
                reject('unable to get token');
            });
        });
    }

    createAccount(email: string, password: string): Promise<void> {
        return new Promise((resolve, reject) => {
            FirebaseAuthentication.createUserWithEmailAndPassword({
                email: email,
                password: password
            }).then((result) => {
                this.logger.debug(`logged in with username / password`);
                resolve();
            }).catch((reason) => {
                reject();
            });
        });
    }

    loginWithUserName(email: string, password: string): void {
        FirebaseAuthentication.signInWithEmailAndPassword({
            email: email,
            password: password
        }).then(() => {
            this.logger.debug(`logged in with email / password`);
        });
    }
}
